import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MainLayout from './MainLayout';
import DataPortalMenuHeader from '../components/DataPortalMenuHeader';
import { useDataPortalStore } from '../store/dataPortalStore';
import { MyDataPortalPage, pickPageName, pickPortalName } from '../utils/dataPortalCache';

const MANAGE_KEY = 'manage';
const UNGROUPED_KEY = 'ungrouped';

/** 一个看板在二级栏里的条目数据 */
interface NamedPage {
    page: MyDataPortalPage;
    name: string;
}

/** 一个分组段落（含「未分组」这个虚拟分组） */
interface GroupSection {
    key: string;
    title: string;
    items: NamedPage[];
}

/**
 * 组内排序：数值越大越靠前，同值按名称本地化排序（与管理页、后端口径一致）。
 * 放模块作用域是因为它只依赖入参 —— 留在组件里会让每次渲染产生新引用，
 * 从而把依赖它的 useCallback / useMemo 全部拖成每次重算。
 */
const sortItems = (items: NamedPage[]) =>
    [...items].sort((a, b) => b.page.sortOrder - a.page.sortOrder || a.name.localeCompare(b.name, 'zh'));

/**
 * 「专题分析」模块布局：二级栏按分组折叠 + 搜索 + 底部管理区。
 *
 * 复用 `MainLayout` 的三个插槽（`secondHeader` 搜索框 / `secondMenu` 分组树 /
 * `secondFooter` 管理入口），而不是自建一整列 —— MainLayout 恒定渲染二级栏那一列，
 * 自建会并排出现两列。
 *
 * **两种形态，由「有可见看板的门户是否多于一个」决定**（见 [layered]）：
 * - 多于一个：三层「门户 → 分组 → 看板」，展开状态由本组件接管
 * - 只有一个、或不是配置人员（后端下发的 `portalCodes` 为空）：两层「分组 → 看板」，
 *   展开状态交还 MainLayout（它会按当前路由自动展开）
 *
 * 门户层**不是授权边界**：它只影响可见看板怎么摆，不影响哪些看板可见 ——
 * 可见性已经在后端按可见范围过滤过了，前端拿到什么就渲染什么。
 */
const DataPortalLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const pages = useDataPortalStore((s) => s.pages);
    const groups = useDataPortalStore((s) => s.groups);
    const portalCodes = useDataPortalStore((s) => s.portalCodes);
    const canConfig = useDataPortalStore((s) => s.canConfig);
    const refresh = useDataPortalStore((s) => s.refresh);

    const [rawQuery, setRawQuery] = useState('');
    const [query, setQuery] = useState('');
    /**
     * 门户分层态下自己维护的展开项。null = 还没手动操作过，用默认展开策略。
     *
     * 必须自己管：MainLayout 内部只支持一层折叠（`findParentKey` 只返回直接父 key、
     * `handleOpenChange` 只保留一个展开项），三层下会出现「展开分组就折叠门户」。
     */
    const [openKeysOverride, setOpenKeysOverride] = useState<string[] | null>(null);

    /**
     * 是否渲染门户分层。
     *
     * 条件是「**有可见看板的门户多于一个**」，不是「有没有门户」：只有一个门户有内容时，
     * 门户那一层不携带任何信息 —— 它下面必然只有一个子项，却要多点一次才展开得到分组。
     * 单门户时直接塌成「分组 → 看板」两层。
     *
     * 用未经搜索过滤的 `pages` 计算，而不是用 `portalSections`（它基于 `matched`）：
     * 后者会让搜索把结果收窄到一个门户时中途塌层，搜索结果的次要信息也跟着丢掉门户名。
     */
    const layered = useMemo(() => {
        const portalsWithPages = new Set(pages.map((page) => page.portalCode));
        return portalCodes.filter((code) => portalsWithPages.has(code)).length > 1;
    }, [pages, portalCodes]);

    // 进入模块时拉一次最新的：管理员刚改过可见范围，用户不该还看到旧列表
    useEffect(() => {
        refresh().catch((e) => console.warn('Refresh data portal pages failed', e));
    }, [refresh]);

    // 输入防抖：几百个看板时每次按键都重算整棵树会掉帧
    useEffect(() => {
        const timer = window.setTimeout(() => setQuery(rawQuery.trim().toLowerCase()), 200);
        return () => window.clearTimeout(timer);
    }, [rawQuery]);

    const named = useMemo(
        () => pages.map((page) => ({ page, name: pickPageName(page, i18n.language) })),
        [pages, i18n.language],
    );

    const matched = useMemo(
        () => (query ? named.filter((item) => item.name.toLowerCase().includes(query)) : named),
        [named, query],
    );

    const boardItem = (item: NamedPage, secondary?: string) => ({
        key: item.page.pageKey,
        display: true,
        label: (
            <span className="flex items-center justify-between gap-2">
                <span className="truncate">{item.page.icon ? `${item.page.icon} ${item.name}` : item.name}</span>
                {secondary && <span className="text-xs text-gray-400 flex-none">{secondary}</span>}
            </span>
        ),
        onClick: () => navigate(`/data-portal/${item.page.pageKey}`),
    });

    /**
     * 构造某个门户下的分组段落；[portalCode] 传 undefined 时不按门户过滤（扁平态）。
     *
     * 「未分组」固定排在末位，且不可重命名/删除（它是虚拟分组，不占分组表的行）。
     * 分层态下未分组段的 key 带门户后缀，否则多个门户的未分组段会撞 key。
     * 未分组看板的门户归属看**看板自己的** portalCode —— 它没有分组可以隐含门户。
     */
    const buildSections = useCallback(
        (items: NamedPage[], portalCode?: string): GroupSection[] => {
            const ordered = [...groups]
                .filter((group) => portalCode == null || group.portalCode === portalCode)
                .sort((a, b) => b.sortOrder - a.sortOrder || Number(a.id) - Number(b.id))
                .map((group) => ({
                    key: `group-${group.id}`,
                    title: pickPageName(group, i18n.language),
                    items: sortItems(items.filter((item) => item.page.groupId === group.id)),
                }));
            const ungrouped = items.filter(
                (item) => !item.page.groupId && (portalCode == null || item.page.portalCode === portalCode),
            );
            return [
                ...ordered,
                {
                    key: portalCode == null ? UNGROUPED_KEY : `${UNGROUPED_KEY}-${portalCode}`,
                    title: t('pages.dataPortal.ungrouped') as string,
                    items: sortItems(ungrouped),
                },
            ].filter((section) => section.items.length > 0);
        },
        [groups, i18n.language, t],
    );

    /** 扁平态（不分层）的分组段落 */
    const flatSections = useMemo(() => buildSections(matched), [buildSections, matched]);

    /**
     * 门户段落。门户顺序直接用后端给的 `portalCodes`（已按 sortOrder 排好），不在前端再排一遍。
     * 门户下一个可见看板都没有时**不渲染这一段** —— 与「组内无可见看板的分组不下发」同一条原则。
     */
    const portalSections = useMemo(
        () =>
            portalCodes
                .map((code) => ({
                    key: `portal-${code}`,
                    title: pickPortalName(code, t),
                    sections: buildSections(matched, code),
                }))
                .filter((portal) => portal.sections.length > 0),
        [portalCodes, buildSections, matched, t],
    );

    /** 分组段落 → 菜单项（含组内看板计数） */
    const sectionToMenuItem = (section: GroupSection) => ({
        key: section.key,
        display: true,
        label: (
            <span className="flex items-center justify-between gap-2">
                <span className="truncate">{section.title}</span>
                <span className="text-xs text-gray-400 flex-none">{section.items.length}</span>
            </span>
        ),
        children: section.items.map((item) => boardItem(item)),
    });

    // 「看板管理」不进这个列表 —— 它是配置入口，性质和「浏览看板」不同，放二级栏底部 footer 单列（见下方 secondFooter）。
    const secondMenu = useMemo(() => {
        if (query) {
            // 搜索态平铺命中项 —— 命中项藏在折叠里等于没搜到。
            // 次要信息在分层态下带上门户名，否则只有分组名无法定位（不同门户可能有同名分组）
            if (layered) {
                return portalSections.flatMap((portal) =>
                    portal.sections.flatMap((section) =>
                        section.items.map((item) => boardItem(item, `${portal.title} · ${section.title}`)),
                    ),
                );
            }
            return flatSections.flatMap((section) =>
                section.items.map((item) => boardItem(item, section.title)),
            );
        }
        if (layered) {
            return portalSections.map((portal) => ({
                key: portal.key,
                display: true,
                label: (
                    <span className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{portal.title}</span>
                        <span className="text-xs text-gray-400 flex-none">
                            {portal.sections.reduce((sum, section) => sum + section.items.length, 0)}
                        </span>
                    </span>
                ),
                children: portal.sections.map(sectionToMenuItem),
            }));
        }
        return flatSections.map(sectionToMenuItem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flatSections, portalSections, layered, query]);

    const manageActive = location.pathname.startsWith('/data-portal/manage');
    const activePageKey = useMemo(
        () => (manageActive ? undefined : location.pathname.match(/^\/data-portal\/([^/]+)/)?.[1]),
        [location.pathname, manageActive],
    );

    /**
     * 当前看板所在的「门户 key + 分组 key」祖先链，用于默认展开到当前项。
     *
     * MainLayout 的 `findParentKey` 只返回直接父级（单个 key），拿不到祖先链 ——
     * 三层下那等于「展开了分组、但它还藏在折叠的门户里」。
     */
    const activeAncestorKeys = useMemo(() => {
        if (!layered || activePageKey == null) return [];
        for (const portal of portalSections) {
            const section = portal.sections.find((s) => s.items.some((i) => i.page.pageKey === activePageKey));
            if (section) return [portal.key, section.key];
        }
        return [];
    }, [layered, activePageKey, portalSections]);

    /**
     * 分层态的展开项。
     *
     * 优先级：手动操作过 → 用手动的；搜索态 → 全展开（命中项不能藏在折叠里）；
     * 否则展开当前看板的祖先链，没有当前看板时展开第一个门户。
     *
     * 扁平态返回 undefined，把展开状态交还 MainLayout —— 它会按当前路由自动展开
     * 当前看板所在的那个分组，比这里再算一遍准。
     */
    const openKeys = useMemo(() => {
        if (!layered) {
            // 扁平态的搜索态仍需全展开，这部分行为与改动前一致
            return query ? flatSections.map((section) => section.key) : undefined;
        }
        if (query) {
            return portalSections.flatMap((portal) => [portal.key, ...portal.sections.map((s) => s.key)]);
        }
        if (openKeysOverride) return openKeysOverride;
        if (activeAncestorKeys.length > 0) return activeAncestorKeys;
        return portalSections.length > 0 ? [portalSections[0].key] : [];
    }, [layered, query, flatSections, portalSections, openKeysOverride, activeAncestorKeys]);

    /**
     * 分层态才接管 onOpenChange。扁平态传 undefined，MainLayout 继续用它自己那套
     * 「展开新菜单只保留一个」的逻辑，行为与改动前完全一致。
     *
     * 搜索态不记录手动操作：搜索期间是强制全展开的，把这期间的折叠动作存进
     * override 会在退出搜索后留下一份莫名其妙的展开状态。
     */
    const handleOpenChange = layered && !query ? setOpenKeysOverride : undefined;

    // 切换看板时清掉手动展开状态，让下次回到「展开当前项的祖先链」这个默认策略。
    // 不清的话点到别的门户的看板后，二级栏仍停在上一个门户展开、当前项不可见的状态。
    useEffect(() => {
        setOpenKeysOverride(null);
    }, [activePageKey]);

    // 落地到侧边栏的第一项，而不是全局 sortOrder 最大的看板 —— 后者会落到跟侧边栏
    // 视觉第一项不一致的位置。分层态按「门户序 → 分组序 → 组内看板序」取，与渲染顺序一致
    const firstPageKey = useMemo(
        () =>
            layered
                ? portalSections[0]?.sections[0]?.items[0]?.page.pageKey
                : flatSections[0]?.items[0]?.page.pageKey,
        [layered, portalSections, flatSections],
    );

    /**
     * 落地与兜底跳转。
     *
     * 除了「停在 /data-portal 根路径」要进第一项，还要处理**当前看板被停用或被移出可见范围**：
     * 此时它已不在 pages 里，二级栏选中项凭空消失、承载页停在「无权限或不存在」的空页，
     * 所以要主动跳到第一个可见看板。
     */
    useEffect(() => {
        if (manageActive) return;
        const isRoot = location.pathname === '/data-portal' || location.pathname === '/data-portal/';
        const currentGone = activePageKey != null && !pages.some((p) => p.pageKey === activePageKey);
        if (!isRoot && !currentGone) return;
        if (firstPageKey) {
            navigate(`/data-portal/${firstPageKey}`, { replace: true });
        } else if (canConfig) {
            navigate(`/data-portal/${MANAGE_KEY}`, { replace: true });
        }
    }, [location.pathname, manageActive, activePageKey, pages, firstPageKey, canConfig, navigate]);

    const hasAnything = pages.length > 0 || canConfig;

    return (
        <MainLayout
            activeFirstMenu="data-portal"
            secondMenu={secondMenu}
            secondMenuOpenKeys={openKeys}
            onSecondMenuOpenChange={handleOpenChange}
            secondHeader={
                <DataPortalMenuHeader value={rawQuery} onChange={setRawQuery} searching={query.length > 0} />
            }
            secondFooter={
                canConfig ? (
                    // 「看板管理」是配置入口，与浏览看板性质不同，单列在二级栏底部，上方加分隔线。
                    <div className="border-t border-gray-200 pt-3">
                        {/* 「管理」小标题 +「管理员可见」标签 */}
                        <div className="flex items-center gap-2 px-1 pb-1">
                            <span className="text-xs text-gray-400">{t('pages.dataPortal.manageSection')}</span>
                            <span className="text-xs font-medium text-purple-600 bg-purple-50 rounded px-1.5 py-0.5">
                                {t('pages.dataPortal.manageVisibleTag')}
                            </span>
                        </div>
                        {/* 配置入口：紫色加粗 + 浅紫底，选中态更深一档 */}
                        <div
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-700 cursor-pointer rounded ${
                                manageActive ? 'bg-purple-100' : 'bg-purple-50 hover:bg-purple-100'
                            }`}
                            onClick={() => navigate(`/data-portal/${MANAGE_KEY}`)}
                        >
                            <SettingOutlined />
                            <span className="truncate">{t('central.dataPortalConfig.title')}</span>
                        </div>
                    </div>
                ) : undefined
            }
        >
            <div className="p-6 h-full flex flex-col">
                {hasAnything ? (
                    <Outlet />
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        {query ? t('pages.dataPortal.noMatch') : t('pages.dataPortal.emptyList')}
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default DataPortalLayout;
